// FlowList.js
import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import Tooltip from '@mui/material/Tooltip';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { DataGrid, GridToolbarContainer, GridActionsCellItem, GridOverlay } from '@mui/x-data-grid';
import { keyframes } from '@emotion/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { createTestFlowAsync } from '../../redux/flows/createFlowSlice';
import { deleteFlowAsync } from '../../redux/flows/deleteFlowSlice';
import {
  fetchFlowsAsync,
  selectAllFlows,
  selectFlowsLoading,
} from '../../redux/flows/getFlowsSlice';

// Service imports
import { filterFlowsByMarket } from '../../services/flowApiService';

// Import components for routing
import FlowEditRouter from './FlowEditRouter';
import EventEdit from './EventEdit';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { dataGridWrapTextStyles } from '../../styles/dataGridStyles';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';

const spinAnimation = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// Custom loading overlay for DataGrid
function CustomLoadingOverlay() {
  return (
    <GridOverlay sx={{ backgroundColor: 'background.default' }}>
      <CircularProgress size="4rem" sx={{ color: 'secondary.main' }} />
    </GridOverlay>
  );
}

const smartFilter = (rows, query) => {
  if (!query) return rows;

  const tokens = query.toLowerCase().trim().split(/\s+/);

  let flowFilter = null;
  let idFilter = null;
  let freeText = [];

  // Parse tokens
  tokens.forEach((token) => {
    if (token.startsWith('flow:')) {
      flowFilter = token.replace('flow:', '');
    } else if (token.startsWith('id:')) {
      idFilter = token.replace('id:', '');
    } else {
      freeText.push(token);
    }
  });

  return rows.filter((row) => {
    const flowName = row.flow_name?.toLowerCase() || '';
    const testId = row.test_case_id?.toLowerCase() || '';

    // AND logic
    if (flowFilter && !flowName.includes(flowFilter)) return false;
    if (idFilter && !testId.includes(idFilter)) return false;

    // text matches either field
    if (
      freeText.length &&
      !freeText.some((text) => flowName.includes(text) || testId.includes(text))
    ) {
      return false;
    }

    return true;
  });
};

function EditToolbar({ onOpenModal, onSetMode, searchText, onSearchChange }) {
  const [open, setOpen] = React.useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = React.useState(false);
  const [isSearchFocused, setIsSearchFocused] = React.useState(false);

  return (
    <GridToolbarContainer
      sx={{
        px: 2,
        py: 1.5,
        gap: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'custom.surfaceAlt',
        alignItems: 'center',
      }}
    >
      <Button
        startIcon={<AddIcon />}
        endIcon={<ArrowDropDownIcon />}
        onClick={() => setOpen(!open)}
        sx={{
          color: 'primary.main',
          '&:hover': { backgroundColor: 'action.hover' },
        }}
      >
        Add Test Case
      </Button>

      {open && (
        <Box
          sx={{
            position: 'absolute',
            mt: 1,
            bgcolor: 'white',
            boxShadow: 3,
            borderRadius: 1,
            zIndex: 50,
            minWidth: 200,
            marginTop: '100px',
          }}
        >
          <MenuItem
            onClick={() => {
              onSetMode('MULTI');
              onOpenModal();
              setOpen(false);
            }}
          >
            Multi-Event Test Case
          </MenuItem>
          <MenuItem
            onClick={() => {
              onSetMode('SINGLE');
              onOpenModal();
              setOpen(false);
            }}
          >
            Single Event Test Case
          </MenuItem>
        </Box>
      )}
      <Tooltip
        arrow
        placement="bottom-start"
        open={isTooltipOpen}
        disableHoverListener
        disableFocusListener
        disableTouchListener
        title={
          <Box sx={{ fontSize: 12 }}>
            <div>
              <strong>Smart search</strong>
            </div>
            <div>• Use keywords or filters to narrow results</div>
            <div>
              • Filter by flow: <code>flow:tf5</code>
            </div>
            <div>
              • Filter by test case: <code>id:menu</code>
            </div>
            <div>
              • Combine for precision: <code>flow:tf5 id:menu</code>
            </div>
          </Box>
        }
      >
        <TextField
          size="small"
          placeholder="Search Flows or Test ID"
          value={searchText}
          onChange={(e) => onSearchChange(e.target.value)}
          onKeyDown={(e) => e.stopPropagation()}
          onMouseEnter={() => {
            if (!isSearchFocused) setIsTooltipOpen(true);
          }}
          onMouseLeave={() => setIsTooltipOpen(false)}
          onFocus={() => {
            setIsSearchFocused(true);
            setIsTooltipOpen(false);
          }}
          onBlur={() => {
            setIsSearchFocused(false);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: searchText && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => onSearchChange('')} sx={{ p: 0.5 }}>
                  <ClearIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            ml: 2,
            width: 320,
            bgcolor: 'custom.surfaceAlt',
            '& .MuiOutlinedInput-root': {
              height: 36,
              borderRadius: 6,
              fontSize: 14,

              // Default (idle)
              '& fieldset': {
                borderColor: 'divider',
                borderWidth: 1,
              },

              // Hover
              '&:hover fieldset': {
                borderColor: 'divider',
              },

              // Focus
              '&.Mui-focused fieldset': {
                borderColor: 'info.light',
                borderWidth: 1,
              },
            },
          }}
        />
      </Tooltip>
    </GridToolbarContainer>
  );
}

export default function FlowList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Redux selectors
  const databaseFlows = useSelector(selectAllFlows);
  const flowsLoading = useSelector(selectFlowsLoading);

  const [rows, setRows] = React.useState([]);
  const [filteredRows, setFilteredRows] = React.useState([]);
  const [searchText, setSearchText] = React.useState('');
  const [selectedMarket, setSelectedMarket] = React.useState(null);
  const [isLoadingDatabase, setIsLoadingDatabase] = React.useState(true);
  const [showSpinner, setShowSpinner] = React.useState(true);
  const [shouldAnimateRows, setShouldAnimateRows] = React.useState(false);
  const isLoadingRef = React.useRef(false);
  const isInitialMount = React.useRef(true);
  const spinnerWasShown = React.useRef(false);

  // Add Flow Modal State
  const [addFlowModalOpen, setAddFlowModalOpen] = React.useState(false);
  const [newFlowName, setNewFlowName] = React.useState('');

  const [createMode, setCreateMode] = React.useState('MULTI');

  // Predefined flow name options
  const flowNameOptions = [
    'Single Event',
    'Ad-Hoc Test',
    'TF1 - GMA - Homepage',
    'TF2 - GMA - Log In',
    'TF3 - GMA - Checkout – Pickup',
    'TF4 - GMA - Deals',
    'TF5 - GMA - Menu',
    'TF6 - GMA - Location',
    'TF7 - GMA - More & Recents',
    'TF8 - GMA - Loyalty',
    'TF9 - GMA – Delivery',
    'TF10 - GMA - Suggestive Sell',
    'TF11 - GMA - Store Closed',
    'TF12 - GMA - User & Loyalty objects',
    'TF13 - GMA – Registration',
    'TF14 - GMA - Checkout - Delivery',
    'TF15 - GMA - Recents & Faves',
    'TF16 - GMA - Code',
  ];
  const [newFlowDescription, setNewFlowDescription] = React.useState('');
  const [newTestCaseId, setNewTestCaseId] = React.useState('');
  const [isCreatingFlow, setIsCreatingFlow] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);

  // Control spinner visibility with delay to prevent flicker on fast loads
  React.useEffect(() => {
    let timer;
    if (isLoadingDatabase) {
      if (isInitialMount.current) {
        // On initial mount, show spinner immediately
        setShowSpinner(true);
        spinnerWasShown.current = true;
        isInitialMount.current = false;
      } else {
        // On subsequent loads, delay spinner to prevent flicker
        timer = setTimeout(() => {
          setShowSpinner(true);
          spinnerWasShown.current = true;
        }, 300);
      }
    } else {
      // When loading finishes, if spinner was shown during THIS load, enable row animation
      if (spinnerWasShown.current) {
        // Force animation restart by toggling off then on
        setShouldAnimateRows(false);
        requestAnimationFrame(() => {
          setShouldAnimateRows(true);
        });
      } else {
        // No spinner shown, ensure animation is off
        setShouldAnimateRows(false);
      }
      setShowSpinner(false);
    }
    return () => clearTimeout(timer);
  }, [isLoadingDatabase]);

  // Reset animation state when navigating away from flow list
  React.useEffect(() => {
    const pathParts = location.pathname.split('/');
    const isShowingFlowList = pathParts.length === 2 && pathParts[1] === 'flows';

    if (!isShowingFlowList) {
      // When navigating to a flow/event, disable animation
      // so rows don't animate when navigating back
      setShouldAnimateRows(false);
    }
  }, [location.pathname]);

  // Load flows from database
  React.useEffect(() => {
    const loadFlows = async () => {
      // Prevent concurrent calls
      if (isLoadingRef.current) {
        return;
      }
      isLoadingRef.current = true;

      // Get current market from localStorage
      const storedMarket = JSON.parse(localStorage.getItem('market')) || null;
      setSelectedMarket(storedMarket);

      // Get database flows via Redux
      setIsLoadingDatabase(true);
      setShouldAnimateRows(false);
      spinnerWasShown.current = false;

      try {
        // Dispatch Redux action to fetch flows
        await dispatch(fetchFlowsAsync()).unwrap();
      } catch (error) {
        console.error('Error loading database flows:', error);
      } finally {
        setIsLoadingDatabase(false);
        isLoadingRef.current = false;
      }
    };

    loadFlows();

    const handleFlowUpdate = () => {
      loadFlows();
    };

    const handleMarketChange = () => {
      loadFlows();
    };

    window.addEventListener('flowUpdated', handleFlowUpdate);
    window.addEventListener('marketChanged', handleMarketChange);

    // Listen for storage changes (market changes from other tabs)
    const handleStorageChange = (e) => {
      if (e.key === 'market') {
        loadFlows();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('flowUpdated', handleFlowUpdate);
      window.removeEventListener('marketChanged', handleMarketChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Process flows from Redux state when they change
  React.useEffect(() => {
    if (!flowsLoading && databaseFlows) {
      const storedMarket = JSON.parse(localStorage.getItem('market')) || null;

      // Filter database flows by market
      const filteredDatabaseFlows = filterFlowsByMarket(databaseFlows, storedMarket?.id);

      // Add sequential IDs for DataGrid
      const flowsWithIds = filteredDatabaseFlows.map((flow, i) => ({
        id: i + 1,
        ...flow,
        isNew: false,
      }));

      setRows(flowsWithIds);
    }
  }, [databaseFlows, flowsLoading, selectedMarket]);

  // Update filtered flow list based on current search input
  React.useEffect(() => {
    setFilteredRows(smartFilter(rows, searchText));
  }, [rows, searchText]);

  // Routing logic
  const pathParts = location.pathname.split('/');

  if (
    pathParts.length >= 6 &&
    pathParts[1] === 'flows' &&
    pathParts[2] === 'edit' &&
    pathParts[4] === 'event'
  ) {
    const flowId = pathParts[3];
    const eventId = pathParts[5];
    return <EventEdit flowId={flowId} eventId={eventId} />;
  }

  if (pathParts.length >= 4 && pathParts[1] === 'flows' && pathParts[2] === 'edit') {
    const flowId = pathParts[3];
    // Use FlowEditRouter to determine which component to show
    return <FlowEditRouter flowId={flowId} />;
  }

  // Modal functions
  const handleAddFlow = () => {
    // Reset modal defaults when opening
    setNewFlowName('');
    setNewFlowDescription('');
    setNewTestCaseId('');
    setCreateMode('MULTI');
    setAddFlowModalOpen(true);
  };

  const closeAddFlowModal = () => {
    setAddFlowModalOpen(false);
    setNewFlowName('');
    setNewFlowDescription('');
    setNewTestCaseId('');
    setCreateMode('MULTI');
  };

  const createFlow = async () => {
    // Prevent double-clicks
    if (isCreatingFlow) return;

    // For SINGLE mode, hardcode flow name to "Single Event"
    const finalFlowName = createMode === 'SINGLE' ? 'Single Event' : newFlowName;

    if (!finalFlowName) {
      alert('Please select a flow name');
      return;
    }
    if (!newFlowDescription) {
      alert('Please enter a flow description');
      return;
    }
    if (!newTestCaseId.trim()) {
      alert('Please enter a test case ID');
      return;
    }
    const existingTestCaseIds = rows.map((row) => row.test_case_id?.toLowerCase());

    if (existingTestCaseIds.includes(newTestCaseId.trim().toLowerCase())) {
      alert(`A test case with the ID "${newTestCaseId}" already exists.`);
      return;
    }

    setIsCreatingFlow(true);
    try {
      // Get current market for the new flow
      const currentMarket = JSON.parse(localStorage.getItem('market')) || null;

      // Dispatch Redux action to create flow in database
      const result = await dispatch(
        createTestFlowAsync({
          name: finalFlowName,
          description: newFlowDescription,
          market: currentMarket?.id || 6,
          test_case_id: newTestCaseId.trim(),
        }),
      );

      if (!result.success) {
        console.error('Error creating flow in database:', result.error);
        alert(`Failed to create flow: ${result.error}`);
        setIsCreatingFlow(false);
        return; // Stop here if API fails
      }

      // Get the flow ID from the result
      const flowDbId = result.flowId;
      if (!flowDbId) {
        console.error('API did not return a flow ID');
        alert('Failed to create flow: No ID returned');
        setIsCreatingFlow(false);
        return;
      }

      // For single event flows, navigate directly to edit screen
      if (createMode === 'SINGLE') {
        await dispatch(fetchFlowsAsync()).unwrap();
        closeAddFlowModal();
        navigate(`/flows/edit/${flowDbId}`);
        return;
      }

      // For multi-event flows: refresh list and close modal
      await dispatch(fetchFlowsAsync()).unwrap();
      closeAddFlowModal();
      alert(`Flow "${finalFlowName}" created successfully!`);
    } catch (error) {
      console.error(error);
      alert(error.message || 'Failed to create flow');
    } finally {
      setIsCreatingFlow(false);
    }
  };

  const handleEditFlow = (flowId) => {
    try {
      const path = `/flows/edit/${flowId}`;
      navigate(path);
    } catch (error) {
      console.error('Navigation error:', error);
    }
  };

  const handleDeleteClick = async (id) => {
    const row = rows.find((r) => r.id === id);

    if (
      window.confirm(
        `Are you sure you want to delete test case "${row.test_case_id}" (${row.flow_name})?`,
      )
    ) {
      setIsDeleting(true);
      try {
        // Delete from database
        const result = await dispatch(deleteFlowAsync(row.flow_db_id));

        if (!result.success) {
          alert(`Failed to delete flow from database: ${result.error}`);
          setIsDeleting(false);
          return;
        }

        // Remove from UI on success
        setRows((prev) => prev.filter((r) => r.id !== id));

        // Trigger refresh to reload from database
        window.dispatchEvent(new Event('flowUpdated'));
      } catch (error) {
        alert(`Error deleting flow: ${error.message}`);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const columns = [
    {
      field: 'flow_name',
      headerName: 'Flow Name',
      flex: 1,
      editable: false,
      cellClassName: 'wrap-text',
      minWidth: 150,
    },
    {
      field: 'test_case_id',
      headerName: 'Test Case ID',
      flex: 1, // increased from 0.6
      editable: false,
      cellClassName: 'wrap-text',
      minWidth: 240,
    },
    {
      field: 'flow_description',
      headerName: 'Description',
      flex: 2, // Increased from 1.5
      editable: false,
      cellClassName: 'wrap-text',
      minWidth: 260,
    },
    {
      field: 'is_active',
      headerName: 'Active',
      type: 'boolean',
      flex: 0.4, // Reduced from 0.5
      editable: false,
      minWidth: 90,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      flex: 0.7, // Reduced from 1
      minWidth: 90,
      getActions: ({ row }) => {
        return [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label="Edit"
            onClick={(e) => {
              e.stopPropagation();
              setTimeout(() => {
                handleEditFlow(row.flow_db_id || row.flow_id || row.id);
              }, 0);
            }}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Delete"
            onClick={() => handleDeleteClick(row.id)}
          />,
        ];
      },
    },
  ];

  return (
    <>
      <Box sx={{ height: '90%', width: '100%' }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          loading={showSpinner || isDeleting}
          initialState={{
            sorting: {
              sortModel: [{ field: 'test_case_id', sort: 'asc' }],
            },
          }}
          disableSelectionOnClick
          disableColumnMenu
          disableColumnFilter
          disableColumnSelector
          getRowHeight={() => 'auto'}
          experimentalFeatures={{ rowHeightAutoAdjustment: true }}
          slots={{
            toolbar: EditToolbar,
            loadingOverlay: CustomLoadingOverlay,
          }}
          slotProps={{
            toolbar: {
              onOpenModal: () => setAddFlowModalOpen(true),
              onSetMode: setCreateMode,
              searchText,
              onSearchChange: setSearchText,
            },
          }}
          sx={{
            ...dataGridWrapTextStyles,
            '& .MuiDataGrid-row': {
              animation: shouldAnimateRows ? 'fadeInRow 0.4s ease-in-out' : 'none',
            },
            '@keyframes fadeInRow': {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
          }}
        />
      </Box>

      {/* Add Flow Modal */}
      <Dialog open={addFlowModalOpen} onClose={closeAddFlowModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {createMode === 'SINGLE' ? 'Add Single Event Test' : 'Add Multi-Event Test Case'}
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* Hide flow name dropdown for single event mode */}
            {createMode !== 'SINGLE' && (
              <FormControl fullWidth>
                <InputLabel id="flow-name-label">Flow Name</InputLabel>
                <Select
                  labelId="flow-name-label"
                  id="flow-name-select"
                  value={newFlowName}
                  label="Flow Name"
                  onChange={(e) => setNewFlowName(e.target.value)}
                  sx={{
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'text.secondary',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>Select a flow type</em>
                  </MenuItem>
                  {flowNameOptions
                    .filter((option) => createMode === 'SINGLE' || option !== 'Single Event')
                    .map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                </Select>
                <FormHelperText>
                  {newFlowName === 'Ad-Hoc Test'
                    ? "Use this for testing individual events that don't belong to a specific flow"
                    : 'Select the type of flow'}
                </FormHelperText>
              </FormControl>
            )}
            <TextField
              label="Test Case ID"
              value={newTestCaseId}
              onChange={(e) => setNewTestCaseId(e.target.value)}
              placeholder="e.g. TC-001"
              fullWidth
              required
              helperText="Unique identifier for the test case"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'text.secondary',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: 'primary.main',
                },
              }}
            />
            <TextField
              label="Test Case Description"
              value={newFlowDescription}
              onChange={(e) => setNewFlowDescription(e.target.value)}
              multiline
              rows={3}
              placeholder="Describe what this test case does..."
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'text.secondary',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: 'primary.main',
                },
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={closeAddFlowModal}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={createFlow}
            disabled={isCreatingFlow}
            sx={{
              bgcolor: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.light',
              },
              '&:disabled': {
                bgcolor: 'action.disabledBackground',
              },
            }}
          >
            {isCreatingFlow
              ? 'Creating...'
              : createMode === 'SINGLE'
                ? 'Create Single Event Test Case'
                : 'Create Flow'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
